import { readonly, shallowRef, type DeepReadonly, type Ref } from 'vue';

export type InstallPromptOutcome = 'accepted' | 'dismissed' | 'unavailable' | 'error';

export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
}

interface InstallPromptEventMap {
  beforeinstallprompt: BeforeInstallPromptEvent;
  appinstalled: Event;
}

interface InstallPromptEventTarget {
  addEventListener<K extends keyof InstallPromptEventMap>(
    type: K,
    listener: (event: InstallPromptEventMap[K]) => void,
  ): void;
}

export interface DeferredInstallPromptController {
  isAvailable: DeepReadonly<Ref<boolean>>;
  isInstalled: DeepReadonly<Ref<boolean>>;
  initialize(): void;
  prompt(): Promise<InstallPromptOutcome>;
}

export const createDeferredInstallPromptController = (
  eventTarget: InstallPromptEventTarget,
): DeferredInstallPromptController => {
  const deferredPrompt = shallowRef<BeforeInstallPromptEvent | null>(null);
  const isAvailable = shallowRef(false);
  const isInstalled = shallowRef(false);
  let initialized = false;

  const initialize = (): void => {
    if (initialized) {
      return;
    }

    initialized = true;
    eventTarget.addEventListener('beforeinstallprompt', (event) => {
      event.preventDefault();
      deferredPrompt.value = event;
      isAvailable.value = true;
    });
    eventTarget.addEventListener('appinstalled', () => {
      deferredPrompt.value = null;
      isAvailable.value = false;
      isInstalled.value = true;
    });
  };

  const prompt = async (): Promise<InstallPromptOutcome> => {
    const event = deferredPrompt.value;
    if (!event) {
      return 'unavailable';
    }

    deferredPrompt.value = null;
    isAvailable.value = false;

    try {
      await event.prompt();
      const choice = await event.userChoice;

      return choice.outcome;
    } catch {
      console.error('[pwa.install] Не удалось завершить системный диалог установки');
      return 'error';
    }
  };

  return {
    isAvailable: readonly(isAvailable),
    isInstalled: readonly(isInstalled),
    initialize,
    prompt,
  };
};

let installPromptController: DeferredInstallPromptController | null = null;

const getInstallPromptController = (): DeferredInstallPromptController => {
  installPromptController ??= createDeferredInstallPromptController(window);

  return installPromptController;
};

export const initializePwaInstallPrompt = (): void => {
  getInstallPromptController().initialize();
};

export const usePwaInstallPrompt = (): DeferredInstallPromptController => getInstallPromptController();
